import { ReactNode } from 'react';

import type {
  IProgressBarMilestone,
  IProgressBarStyleConfig,
  IProgressBarTextContent
} from '@appcharge/shared-ui';
import {
  BundlesLayout,
  BuyOneGetMany,
  createGradientOrSolid,
  DefaultDailyBonus,
  DefaultRewardCalendar,
  EBundlesViewModel,
  EFontWeights,
  EProgressBarType as ESharedProgressBarType,
  HugeRollingOffer,
  ProgressBar,
  StoreRefreshPopup
} from '@appcharge/shared-ui';
import {
  EBundlesInternalViewModel,
  EProgressBarType,
  ERewardCalendarPopUpTrigger,
  ESpecialOfferInternalViewModel
} from 'constants/enums';
import { getMilestonePreviewClaimedCount } from 'utils/milestonePreview';
import { renderingUtil } from 'utils/renderingUtil';
import {
  MOCK_PRICES,
  MOCK_PROGRESS_BAR_MILESTONE_SEGMENTS,
  MOCK_PROGRESS_BAR_PRODUCTS,
  MOCK_ROLLING_OFFERS_PRODUCTS,
  POPUP_DEFAULTS,
  PROGRESS_BAR_DEFAULTS,
  PROGRESS_BAR_PREVIEW_DEFAULTS,
  PROMOTION_DEFAULTS,
  REWARD_CALENDAR_DEFAULTS,
  ROLLING_OFFER_DEFAULTS,
  TRIGGERED_POPUP_DEFAULTS
} from 'views/OfferDesign/OfferDesign.consts';
import {
  GradientColor,
  RenderCardFn
} from 'views/OfferDesign/OfferDesign.types';
import {
  normalizeBgColor,
  toNumberWithFallback
} from 'views/OfferDesign/OfferDesign.utils';

import { PREVIEW_ELEMENT_ID } from './OfferDesignPreview.consts';

const BUY_ONE_GET_MANY_PREVIEW_INDEX_TO_COLLECT = 0;
const STANDARD_PREVIEW_INDEX_TO_COLLECT = 1;

export const applyCssVariables = (
  el: HTMLDivElement,
  storeScreen: any,
  font: string
) => {
  el.style.setProperty(
    '--button-text-color',
    storeScreen.buttonTextColor || ''
  );
  el.style.setProperty(
    '--button-bg-color',
    createGradientOrSolid(storeScreen.buttonColor) || ''
  );
  el.style.setProperty('--font-family', font || '');
};

export const clearCssVariables = (el: HTMLDivElement) => {
  el.style.removeProperty('--button-text-color');
  el.style.removeProperty('--button-bg-color');
  el.style.removeProperty('--font-family');
};

export const renderBundleCard: RenderCardFn = (
  values,
  generalTheme,
  storeScreen
): ReactNode => {
  const viewModel = generalTheme.bundlesViewModel as EBundlesViewModel;
  const internalModel =
    generalTheme.bundlesInternalViewModel || EBundlesInternalViewModel.MAIN_SUB;

  return (
    <BundlesLayout bundlesViewModel={viewModel} isPreview>
      {renderingUtil.renderBundleByInternalModel(internalModel, {
        index: 0,
        ...(values.backgroundImage
          ? { backgroundSrc: values.backgroundImage }
          : {}),
        ...(values.backgroundColor
          ? { backgroundColor: normalizeBgColor(values.backgroundColor) }
          : {}),
        bundleBorderColor: storeScreen.bundleBorderColor,
        productList: [],
        bundleButtonColor: storeScreen.buttonColor,
        bundleButtonTextColor: storeScreen.buttonTextColor,
        fontFamily: generalTheme.font,
        prices: MOCK_PRICES,
        inMobileMock: true,
        gameName: internalModel
      })}
    </BundlesLayout>
  );
};

export const renderPromotionCard: RenderCardFn = (
  values,
  generalTheme,
  storeScreen
): ReactNode => {
  const internalModel =
    generalTheme.specialOffersInternalViewModel ||
    ESpecialOfferInternalViewModel.HUGE;

  return renderingUtil.renderSpecialOfferByInternalModel(internalModel, {
    title: values.title || '',
    fontSize: values.fontSize || PROMOTION_DEFAULTS.fontSize,
    fontWeight: (values.fontWeight ||
      PROMOTION_DEFAULTS.fontWeight) as EFontWeights,
    textColor: values.fontColor || PROMOTION_DEFAULTS.fontColor,
    subTitle: values.subTitleText || '',
    subTitleSize: values.subTitleFontSize || PROMOTION_DEFAULTS.subFontSize,
    subTitleWeight: (values.subTitleFontWeight ||
      PROMOTION_DEFAULTS.fontWeight) as EFontWeights,
    subTitleColor: values.subTitleFontColor || PROMOTION_DEFAULTS.fontColor,
    ...(values.backgroundImage
      ? { backgroundImage: values.backgroundImage }
      : {}),
    ...(values.backgroundColor
      ? { backgroundColor: normalizeBgColor(values.backgroundColor) }
      : {}),
    borderColor: values.borderColor,
    buttonColor: storeScreen.buttonColor,
    design: 'match',
    prices: MOCK_PRICES,
    productsList: [],
    selectOffer: () => {},
    collectText: values.collectText || PROMOTION_DEFAULTS.collectText
  });
};

const buildPreviewMilestones = (): IProgressBarMilestone[] => {
  const rewardImage = MOCK_PROGRESS_BAR_PRODUCTS.products[0].images.product;
  const claimedCount = getMilestonePreviewClaimedCount(
    MOCK_PROGRESS_BAR_MILESTONE_SEGMENTS.length
  );
  const cumulativePoints = MOCK_PROGRESS_BAR_MILESTONE_SEGMENTS.reduce<number[]>(
    (acc, segment) => {
      const previousTotal = acc.length ? acc[acc.length - 1] : 0;
      return [...acc, previousTotal + segment.barSize];
    },
    []
  );

  return MOCK_PROGRESS_BAR_MILESTONE_SEGMENTS.map((segment, index) => ({
    points: cumulativePoints[index],
    label: segment.label,
    rewardImage,
    isCollected: index < claimedCount,
    isGrandPrize: index === MOCK_PROGRESS_BAR_MILESTONE_SEGMENTS.length - 1
  }));
};

export const renderProgressBarCard: RenderCardFn = (values) => {
  const styleConfig: IProgressBarStyleConfig = {
    backgroundOpacity: toNumberWithFallback(
      values.backgroundOpacity,
      PROGRESS_BAR_DEFAULTS.backgroundOpacity
    ),
    frameRadius: toNumberWithFallback(
      values.frameRadius,
      PROGRESS_BAR_DEFAULTS.frameRadius
    ),
    barFillColor:
      normalizeBgColor(values.barFillColor) ||
      PROGRESS_BAR_DEFAULTS.barFillColor,
    backgroundColor:
      normalizeBgColor(values.barBackgroundColor) ||
      PROGRESS_BAR_DEFAULTS.backgroundColor,
    barRadius: toNumberWithFallback(
      values.barRadius,
      PROGRESS_BAR_DEFAULTS.barRadius
    ),
    borderColor:
      normalizeBgColor(values.borderColor) || PROGRESS_BAR_DEFAULTS.borderColor,
    borderWidth: toNumberWithFallback(
      values.borderWidth,
      PROGRESS_BAR_DEFAULTS.borderWidth
    ),
    textColor: values.textColor || PROGRESS_BAR_DEFAULTS.textColor
  };

  const isMilestone = values.progressBarType === EProgressBarType.MILESTONE;

  if (isMilestone) {
    const milestones = buildPreviewMilestones();
    const totalPoints = milestones[milestones.length - 1].points;
    const claimedCount = getMilestonePreviewClaimedCount(milestones.length);
    let collectedValue = 0;
    if (claimedCount > 0) {
      collectedValue = milestones[claimedCount - 1].points;
    }
    let progress = 0;
    if (totalPoints > 0) {
      progress = (collectedValue / totalPoints) * 100;
    }

    const title: IProgressBarTextContent = {
      text: values.title,
      fontSize: values.fontSize,
      fontWeight: values.fontWeight,
      color: values.textColor
    };

    const subtitle: IProgressBarTextContent = {
      text: values.subTitleText,
      fontSize: values.subTitleFontSize,
      fontWeight: values.subTitleFontWeight,
      color: values.textColor
    };

    return (
      <ProgressBar
        type={ESharedProgressBarType.MILESTONE}
        milestones={milestones}
        totalPoints={totalPoints}
        currentValue={collectedValue}
        maxValue={totalPoints}
        progress={progress}
        title={title}
        subtitle={subtitle}
        styleConfig={styleConfig}
        leftImageUrl={values.accumulatedItemImageUrl || ''}
        accumulatedItemBackgroundImage={
          values.accumulatedItemBackgroundImageUrl || ''
        }
        collectedIconImage={values.collectedIconImage || ''}
        tooltipBackgroundImage={values.tooltipBackgroundImage || ''}
        barBackgroundImage={values.barBackgroundImage || ''}
        headerImage={values.headerImage || ''}
        isPreview
      />
    );
  }

  return (
    <ProgressBar
      progress={
        (PROGRESS_BAR_PREVIEW_DEFAULTS.collectedPoints /
          MOCK_PROGRESS_BAR_PRODUCTS.barSize) *
        100
      }
      currentValue={PROGRESS_BAR_PREVIEW_DEFAULTS.collectedPoints}
      maxValue={MOCK_PROGRESS_BAR_PRODUCTS.barSize}
      styleConfig={styleConfig}
      leftImageUrl={values.accumulatedItemImageUrl || ''}
      rightImageUrl={MOCK_PROGRESS_BAR_PRODUCTS.products[0].images.product}
      rightLabel={MOCK_PROGRESS_BAR_PRODUCTS.products[0].quantityDisplay}
      barBackgroundImage={values.barBackgroundImage || ''}
      headerImage={values.headerImage || ''}
      isPreview
    />
  );
};

const DEFAULT_BUY_ONE_GET_MANY_FREE_TILES_COLUMNS = 2;

const buildBuyOneGetManyDesignPreviewProducts = (freeTilesColumns: number) =>
  MOCK_ROLLING_OFFERS_PRODUCTS.slice(0, 1 + freeTilesColumns).map(
    (product, position) => ({ ...product, index: position })
  );

export const renderBuyOneGetManyCard: RenderCardFn = (
  values,
  _generalTheme,
  storeScreen
) => {
  const freeTilesColumns =
    values.freeTilesLayout === undefined
      ? DEFAULT_BUY_ONE_GET_MANY_FREE_TILES_COLUMNS
      : Number(values.freeTilesLayout);

  return (
    <BuyOneGetMany
      isPreview
      isPermanent
      offerId="offer-design-buy-one-get-many-preview"
      indexToCollect={BUY_ONE_GET_MANY_PREVIEW_INDEX_TO_COLLECT}
      headerImage={values.headerImage}
      backgroundColor={
        normalizeBgColor(values.backgroundColor) ||
        ROLLING_OFFER_DEFAULTS.backgroundColor
      }
      subOfferUiProps={{
        backgroundColor:
          normalizeBgColor(values.offersBackgroundColor) ||
          ROLLING_OFFER_DEFAULTS.subOfferBackgroundColor,
        backgroundImage: values.offersBackgroundImage || undefined,
        freeTilesBackgroundImage:
          values.freeTilesBackgroundImage || undefined,
        lockImage: values.offersLockImage || ROLLING_OFFER_DEFAULTS.lockImage,
        buttonColor: storeScreen.buttonColor,
        collectText: values.collectText || ROLLING_OFFER_DEFAULTS.collectText,
        buttonTextColor: storeScreen.buttonTextColor
      }}
      selectOffer={() => undefined}
      subOffersProducts={buildBuyOneGetManyDesignPreviewProducts(
        freeTilesColumns
      )}
      plusImage={values.plusImage}
      freeTilesColumns={freeTilesColumns}
      collectedCheckmark={
        values.claimedIconImage
          ? { imageUrl: values.claimedIconImage }
          : undefined
      }
    />
  );
};

export const renderRollingOfferCard: RenderCardFn = (
  values,
  _generalTheme,
  storeScreen
) => {
  const backgroundOpacity =
    values.backgroundOpacity != null
      ? Number(values.backgroundOpacity)
      : ROLLING_OFFER_DEFAULTS.backgroundOpacity;
  const borderWidth =
    values.borderWidth != null
      ? Number(values.borderWidth)
      : ROLLING_OFFER_DEFAULTS.borderWidth;

  const sharedProps = {
    isPreview: true as const,
    isPermanent: true,
    backgroundImage: values.backgroundImage,
    horizontalBackgroundImage: values.horizontalBackgroundImage,
    headerImage: values.headerImage,
    backgroundOpacity,
    backgroundColor:
      normalizeBgColor(values.backgroundColor) ||
      ROLLING_OFFER_DEFAULTS.backgroundColor,
    borderColor:
      normalizeBgColor(values.borderColor) ||
      ROLLING_OFFER_DEFAULTS.borderColor,
    borderWidth,
    collectBehavior: 'disappear' as const,
    subRollingOfferUiProps: {
      backgroundColor:
        normalizeBgColor(values.offersBackgroundColor) ||
        ROLLING_OFFER_DEFAULTS.subOfferBackgroundColor,
      backgroundImage: values.offersBackgroundImage || undefined,
      lockImage: values.offersLockImage || ROLLING_OFFER_DEFAULTS.lockImage,
      buttonColor: storeScreen.buttonColor,
      collectText: values.collectText || ROLLING_OFFER_DEFAULTS.collectText,
      buttonTextColor: storeScreen.buttonTextColor
    },
    selectOffer: () => undefined
  };

  return (
    <HugeRollingOffer
      {...sharedProps}
      indexToCollect={STANDARD_PREVIEW_INDEX_TO_COLLECT}
      arrowColor={values.arrowColor || ROLLING_OFFER_DEFAULTS.arrowColor}
      arrowImage={values.arrowImage || ''}
      enableScroll={Boolean(values.scrollArrowImage)}
      scrollArrowImage={values.scrollArrowImage || ''}
      subRollingOffersProducts={MOCK_ROLLING_OFFERS_PRODUCTS}
    />
  );
};

export const renderPopupCard: RenderCardFn = (values) => {
  return (
    <DefaultDailyBonus
      isPreview
      title={values.title || ''}
      backgroundImage={values.backgroundImage}
      titleWeight={
        (values.fontWeight || POPUP_DEFAULTS.fontWeight) as EFontWeights
      }
      titleSize={(values.fontSize || POPUP_DEFAULTS.fontSize) + 'px'}
      titleColor={
        values.fontColor?.colorOne || POPUP_DEFAULTS.fontColor.colorOne
      }
      onCollect={async () => true}
      borderColor={
        normalizeBgColor(values.borderColor) || POPUP_DEFAULTS.borderColor
      }
      borderWidth={
        values.borderWidth != null
          ? Number(values.borderWidth)
          : POPUP_DEFAULTS.borderWidth
      }
      modalContainer={() => document.getElementById(PREVIEW_ELEMENT_ID)}
      dataTestId="popup-preview"
    />
  );
};

const EMPTY_REWARD_CALENDAR_SEQUENCE = Array.from({ length: 7 }, (_, index) => ({
  index,
  products: []
}));

export const renderRewardCalendarCard: RenderCardFn = (values) => {
  return (
    <DefaultRewardCalendar
      isPreview
      showCloseButton={false}
      dataTestId="reward-calendar-preview"
      buttonText="Collect"
      isAllowedToCollect
      indexToCollect={2}
      productsSequence={EMPTY_REWARD_CALENDAR_SEQUENCE}
      rewardCalendar={{
        unlockedIndex: 2,
        nextUnlockAt: new Date(Date.now() + 60 * 60 * 1000),
        popupTrigger: ERewardCalendarPopUpTrigger.SHOW_AUTOMATICALLY as never
      }}
      rewardCalendarUi={{
        headerImage: values.headerImage || '',
        collectibleBadgeImage:
          values.collectibleBadgeImage ||
          REWARD_CALENDAR_DEFAULTS.collectibleBadgeImage,
        lockedBadgeImage:
          values.lockedBadgeImage || REWARD_CALENDAR_DEFAULTS.lockedBadgeImage,
        backgroundColor:
          values.backgroundColor || REWARD_CALENDAR_DEFAULTS.backgroundColor,
        dayFontColor:
          values.dayFontColor || REWARD_CALENDAR_DEFAULTS.dayFontColor,
        subRewardBackgroundImage: values.subRewardBackgroundImage || '',
        mainRewardBackgroundImage: values.mainRewardBackgroundImage || '',
        collectedIconImage:
          values.collectIconImage || REWARD_CALENDAR_DEFAULTS.collectIconImage,
        lockedIconImage:
          values.lockedIconImage || REWARD_CALENDAR_DEFAULTS.lockedIconImage
      }}
      onCollect={() => Promise.resolve(true)}
      modalContainer={() => document.getElementById(PREVIEW_ELEMENT_ID)}
    />
  );
};

export const renderTriggeredPopupCard: RenderCardFn = (values) => {
  const bgColorOne =
    typeof values.backgroundColor === 'object'
      ? (values.backgroundColor as GradientColor).colorOne
      : values.backgroundColor;

  return (
    <StoreRefreshPopup
      open
      disableBodyLock
      onClose={() => {}}
      onCollect={() => {}}
      title={values.title || ''}
      titleColor={
        values.fontColor?.colorOne ||
        TRIGGERED_POPUP_DEFAULTS.fontColor.colorOne
      }
      titleWeight={values.fontWeight || TRIGGERED_POPUP_DEFAULTS.fontWeight}
      backgroundColor={
        bgColorOne || TRIGGERED_POPUP_DEFAULTS.backgroundColor.colorOne
      }
      backgroundImage={values.backgroundImage}
      buttonText={MOCK_PRICES.priceDisplay}
      products={values?.products || []}
      container={document.getElementById(PREVIEW_ELEMENT_ID)}
    />
  );
};
